import React, { useState } from 'react';

const API_BASE_URL = 'https://web-production-79a0.up.railway.app/api';

interface OTPAuthProps {
  onSuccess: (token: string, partner: any) => void;
}

const OTPAuth: React.FC<OTPAuthProps> = ({ onSuccess }) => {
  const [step, setStep] = useState<'phone' | 'otp' | 'details'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [partnerDetails, setPartnerDetails] = useState({
    name: '',
    email: '',
    vehicleType: 'Bike',
    vehicleNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Format phone with country code
      let formattedPhone = phone.trim();
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+91' + formattedPhone;
      }

      console.log('Sending OTP to:', formattedPhone);
      
      const response = await fetch(`${API_BASE_URL}/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: formattedPhone })
      });

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Response data:', result);
      
      if (response.ok && result.success) {
        setPhone(formattedPhone);
        setStep('otp');
        alert('OTP sent to your phone! Check SMS.');
      } else {
        setError(result.message || `Failed to send OTP (${response.status})`);
      }
    } catch (err: any) {
      console.error('OTP send error:', err);
      setError(`Network error: ${err.message || 'Please check your connection'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (otp.length !== 6) {
        setError('Please enter a valid 6-digit OTP');
        setLoading(false);
        return;
      }

      const verifyResponse = await fetch(`${API_BASE_URL}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phone, otp })
      });

      const verifyResult = await verifyResponse.json();
      
      if (verifyResult.success) {
        // Check if partner exists with this phone
        const loginResponse = await fetch(`${API_BASE_URL}/partners/login-phone`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone })
        });
        
        if (loginResponse.ok) {
          // Partner exists, log them in
          const partnerData = await loginResponse.json();
          localStorage.setItem('token', partnerData.token);
          onSuccess(partnerData.token, partnerData);
        } else {
          // New partner, collect details
          setStep('details');
        }
      } else {
        setError(verifyResult.message || 'Invalid OTP');
      }
    } catch (err: any) {
      console.error('OTP verify error:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/partners/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...partnerDetails,
          phone,
          password: Math.random().toString(36).slice(-8) // Generate random password
        })
      });

      if (response.ok) {
        const partnerData = await response.json();
        localStorage.setItem('token', partnerData.token);
        onSuccess(partnerData.token, partnerData);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Registration failed');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.icon}>🚴</div>
          <h2 style={styles.title}>Partner Login with OTP</h2>
          <p style={styles.subtitle}>Quick & secure phone verification</p>
        </div>

        {/* Step 1: Enter Phone */}
        {step === 'phone' && (
          <form onSubmit={handleSendOTP} style={styles.form}>
            <div style={styles.stepIndicator}>Step 1: Enter Phone</div>
            
            <input
              type="tel"
              placeholder="Phone Number (10 digits)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              pattern="[0-9]{10}"
              style={styles.input}
            />

            <div style={styles.note}>
              We'll send a 6-digit OTP to verify your number
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
            >
              {loading ? '📱 Sending...' : '📱 Send OTP'}
            </button>
          </form>
        )}

        {/* Step 2: Verify OTP */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOTP} style={styles.form}>
            <div style={styles.stepIndicator}>Step 2: Verify OTP</div>
            
            <div style={styles.successBox}>
              ✅ OTP sent to {phone}
            </div>

            <input
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
              maxLength={6}
              pattern="[0-9]{6}"
              style={styles.input}
              autoFocus
            />

            <div style={styles.note}>
              Check your SMS for the verification code
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
            >
              {loading ? '🔄 Verifying...' : '✅ Verify OTP'}
            </button>

            <button
              type="button"
              onClick={() => handleSendOTP({ preventDefault: () => {} } as any)}
              style={styles.resendButton}
            >
              Resend OTP
            </button>
          </form>
        )}

        {/* Step 3: Partner Details (New Registration) */}
        {step === 'details' && (
          <form onSubmit={handleCompleteRegistration} style={styles.form}>
            <div style={styles.stepIndicator}>Step 3: Partner Details</div>
            
            <div style={styles.successBox}>
              ✅ Phone Verified! Complete your profile
            </div>

            <input
              type="text"
              placeholder="Full Name"
              value={partnerDetails.name}
              onChange={(e) => setPartnerDetails({...partnerDetails, name: e.target.value})}
              required
              style={styles.input}
            />

            <input
              type="email"
              placeholder="Email Address"
              value={partnerDetails.email}
              onChange={(e) => setPartnerDetails({...partnerDetails, email: e.target.value})}
              required
              style={styles.input}
            />

            <select
              value={partnerDetails.vehicleType}
              onChange={(e) => setPartnerDetails({...partnerDetails, vehicleType: e.target.value})}
              style={styles.input}
              required
            >
              <option value="Bike">🏍️ Bike</option>
              <option value="Scooter">🛵 Scooter</option>
              <option value="Car">🚗 Car</option>
              <option value="Bicycle">🚲 Bicycle</option>
            </select>

            <input
              type="text"
              placeholder="Vehicle Number (e.g., TS09AB1234)"
              value={partnerDetails.vehicleNumber}
              onChange={(e) => setPartnerDetails({...partnerDetails, vehicleNumber: e.target.value})}
              required
              style={styles.input}
            />

            {error && <div style={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
            >
              {loading ? '⏳ Creating Account...' : '🚀 Complete Registration'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    padding: '20px'
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    maxWidth: '450px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '30px'
  },
  icon: {
    fontSize: '60px',
    marginBottom: '15px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700' as const,
    color: '#282c3f',
    margin: '10px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: '5px 0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '15px'
  },
  stepIndicator: {
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600' as const,
    textAlign: 'center' as const,
    width: 'fit-content',
    margin: '0 auto'
  },
  input: {
    padding: '14px 16px',
    border: '2px solid #e9e9eb',
    borderRadius: '10px',
    fontSize: '15px',
    transition: 'all 0.3s ease',
    outline: 'none'
  },
  button: {
    padding: '14px',
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '10px'
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  resendButton: {
    background: 'transparent',
    color: '#4CAF50',
    border: '2px solid #4CAF50',
    padding: '12px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer'
  },
  successBox: {
    background: '#d4edda',
    border: '1px solid #28a745',
    color: '#155724',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    textAlign: 'center' as const
  },
  note: {
    fontSize: '13px',
    color: '#666',
    textAlign: 'center' as const
  },
  error: {
    background: '#f8d7da',
    color: '#721c24',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    border: '1px solid #f5c6cb'
  }
};

export default OTPAuth;
